/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 30 2023
 **/

import React, { ComponentProps, useState } from "react";
import { css } from "@emotion/react";

import { filterHTMLAttribute } from "@/frameworks/libraries/utilities";

import { Typography, Image } from "@/frameworks/designStuff/components";

export interface AvatarBaseProps extends React.HTMLAttributes<HTMLDivElement> {
	size?: UnitString;
	borderRadius?: UnitString;
}

type ImageAvatarProps = AvatarBaseProps & {
	type: "image";
	onErrorImage?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
} & React.ImgHTMLAttributes<HTMLImageElement>;

type TextAvatarProps = AvatarBaseProps & {
	type: "text";
	typoSize: ComponentProps<typeof Typography>["typoSize"];
	text?: string;
	textColor?: string;
	backgroundColor?: string;
} & React.HTMLAttributes<HTMLDivElement>;

/**
 * flexible avatar component. figma용 design system은 Avatar Component를 사용하세요.
 * 예외 사항 및 복잡한 아바타 로직을 처리할때 사용하세요.
 * @param param [type] image or text
 * @param param [size] width & height
 * @param param [borderRadius] border-radius
 * @param param [src] type===image 일 경우의 이미지 리소스
 * @param param [alt] type===image 일 경우의 image alt
 * @param param [text] type===text 일 경우의 text
 * @param param [textColor] type===text 일 경우의 text color
 * @param param [backgroundColor] type===text 일 경우의 background color
 * @param param [typoSize] type===text 일 경우의 text size
 * @returns
 */
export default function AvatarBase({
	type = "image",
	size = "100%",
	...rest
}: AvatarBaseProps & (ImageAvatarProps | TextAvatarProps)) {
	if (type === "image") {
		return ImageAvatar({ type, size, ...rest });
	}
	if (type === "text") {
		return TextAvatar({ type, size, ...rest } as TextAvatarProps);
	}
	return null;
}

const defaultImageOptimize = {
	w: 64,
	h: 64
};

function ImageAvatar({ size = "100%", borderRadius, src, alt, onErrorImage, ...rest }: ImageAvatarProps) {
	const [imgCss, setImgCss] = useState({
		width: "100%",
		height: "100%"
	});

	return (
		<>
			<div css={[defaultCss(size, borderRadius)]} {...filterHTMLAttribute(rest)}>
				{src ? (
					<Image
						css={{
							width: imgCss.width,
							height: imgCss.height,
							objectFit: "cover"
						}}
						optimize={defaultImageOptimize}
						src={src}
						onLoad={(e) => {
							const img = e.target as HTMLImageElement;

							const widthIsLong = img.naturalWidth > img.naturalHeight;
							const heightIsLong = img.naturalWidth < img.naturalHeight;

							if (widthIsLong) {
								setImgCss({
									width: "auto",
									height: "100%"
								});
							} else if (heightIsLong) {
								setImgCss({
									width: "100%",
									height: "auto"
								});
							} else {
								setImgCss({
									width: "100%",
									height: "100%"
								});
							}
						}}
						alt={alt}
						onError={onErrorImage}
					/>
				) : null}
			</div>
		</>
	);
}

function TextAvatar({ size = "100%", borderRadius, text, ...rest }: TextAvatarProps) {
	return (
		<>
			<div
				css={[defaultCss(size, borderRadius), textCss(rest.textColor, rest.backgroundColor)]}
				{...filterHTMLAttribute(rest)}
			>
				{text ? (
					<Typography align="center" typoSize={rest.typoSize} typoColor={rest.textColor as keyof Colors}>
						{text}
					</Typography>
				) : null}
			</div>
		</>
	);
}

const defaultCss = (size: AvatarBaseProps["size"], borderRadius: AvatarBaseProps["borderRadius"]) => css`
	width: ${size};
	height: ${size};
	/* min-width: ${size};
	min-height: ${size}; */
	border-radius: ${borderRadius ? borderRadius : size};
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #eee;
`;

const textCss = (textColor: TextAvatarProps["textColor"], backgroundColor: TextAvatarProps["backgroundColor"]) => css`
	text-align: center;
	color: ${textColor ? textColor : "#fff"};
	background-color: ${backgroundColor ? backgroundColor : "#aaa"};
	word-break: break-all;
`;
